import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showAssignGuestForm, setShowAssignGuestForm] = useState(false);
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0);
  
  // State for fetched data
  const [stats, setStats] = useState([
    { icon: '🛏️', title: 'Total Rooms', value: '0', change: '+0%', changeType: 'increase', color: '#4CAF50' },
    { icon: '📅', title: 'Occupied Rooms', value: '0', change: '+0%', changeType: 'increase', color: '#f44336' },
    { icon: '👥', title: 'Total Guests', value: '0', change: '+0%', changeType: 'increase', color: '#FF9800' },
    { icon: '💰', title: 'Revenue (This Month)', value: '₹0', change: '+0%', changeType: 'increase', color: '#9C27B0' },
  ]);
  
  const [floors, setFloors] = useState([]);
  const [roomsData, setRoomsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [assignGuestData, setAssignGuestData] = useState({
    bookingType: 'Single',
    numberOfGuests: 1,
    guests: [{
      guestName: '',
      age: '',
      gender: 'Male',
      phone: '',
      email: '',
      address: '',
      idType: 'Aadhaar Card',
      idNumber: '',

    }],
    checkIn: '',
    checkOut: '',
    bookingAmount: '',
  });

  // Booking type configurations
  const bookingTypeConfig = {
    'Single': { 
      minGuests: 1, 
      maxGuests: 1, 
      label: 'Single',
      icon: '👤',
      description: 'One guest'
    },
    'Couple': { 
      minGuests: 2, 
      maxGuests: 2, 
      label: 'Couple',
      icon: '💑',
      description: 'Married couple'
    },
    'Unmarried Couple': { 
      minGuests: 2, 
      maxGuests: 2, 
      label: 'Unmarried Couple',
      icon: '👫',
      description: 'Unmarried couple'
    },
    'Family': { 
      minGuests: 2, 
      maxGuests: 10, 
      label: 'Family',
      icon: '👨‍👩‍👧‍👦',
      description: 'Family members'
    },
    'Group of Friends': { 
      minGuests: 2, 
      maxGuests: 10, 
      label: 'Group of Friends',
      icon: '👥',
      description: 'Friends group'
    },
  };

  // Room amenities for each room type
  const roomAmenities = {
    'Standard': {
      size: '250 sq ft',
      bedType: 'Queen Size Bed',
      maxOccupancy: 2,
      amenities: [
        '✓ Air Conditioning',
        '✓ LED TV with Cable',
        '✓ Free Wi-Fi',
        '✓ Attached Bathroom',
        '✓ Hot & Cold Water',
        '✓ Room Service',
        '✓ Daily Housekeeping',
        '✓ Wardrobe',
      ],
      price: '₹3,000 - ₹3,500 per night',
      images: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&q=80',
    },
    'Deluxe': {
      size: '350 sq ft',
      bedType: 'King Size Bed',
      maxOccupancy: 3,
      amenities: [
        '✓ Air Conditioning',
        '✓ Smart TV with Premium Channels',
        '✓ High-Speed Wi-Fi',
        '✓ Attached Bathroom with Bathtub',
        '✓ Mini Fridge',
        '✓ Tea/Coffee Maker',
        '✓ Room Service (24/7)',
        '✓ Daily Housekeeping',
        '✓ Work Desk',
        '✓ Wardrobe',
        '✓ Balcony',
      ],
      price: '₹4,000 - ₹4,500 per night',
      images: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&q=80',
    },
    'Suite': {
      size: '500 sq ft',
      bedType: 'King Size Bed + Sofa Bed',
      maxOccupancy: 4,
      amenities: [
        '✓ Central Air Conditioning',
        '✓ 55" Smart TV',
        '✓ Premium Wi-Fi',
        '✓ Luxury Bathroom with Jacuzzi',
        '✓ Mini Bar',
        '✓ Tea/Coffee Maker',
        '✓ Microwave',
        '✓ Room Service (24/7)',
        '✓ Daily Housekeeping (Twice)',
        '✓ Living Area',
        '✓ Work Desk',
        '✓ Large Wardrobe',
        '✓ Private Balcony',
        '✓ Complimentary Breakfast',
      ],
      price: '₹5,500 - ₹6,500 per night',
      images: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80',
    },
  };

  // Fetch dashboard stats
  useEffect(() => {
    fetchDashboardStats();
    fetchAllRooms();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/stats');
      const data = await response.json();
      
      if (data.success) {
        setStats([
          { 
            icon: '🛏️', 
            title: 'Total Rooms', 
            value: data.stats.totalRooms.toString(), 
            change: '+0%', 
            changeType: 'increase', 
            color: '#4CAF50' 
          },
          { 
            icon: '📅', 
            title: 'Occupied Rooms', 
            value: data.stats.occupiedRooms.toString(), 
            change: '+12%', 
            changeType: 'increase', 
            color: '#f44336' 
          },
          { 
            icon: '👥', 
            title: 'Total Guests', 
            value: data.stats.totalGuests.toString(), 
            change: '+8%', 
            changeType: 'increase', 
            color: '#FF9800' 
          },
          { 
            icon: '💰', 
            title: 'Revenue (This Month)', 
            value: `₹${data.stats.monthlyRevenue.toLocaleString('en-IN')}`, 
            change: '+15%', 
            changeType: 'increase', 
            color: '#9C27B0' 
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAllRooms = async () => {
    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/rooms');
      const data = await response.json();
      
      if (data.success) {
        setRoomsData(data.rooms);
        
        // Group rooms by floor
        const floorsMap = {};
        data.rooms.forEach(room => {
          if (!floorsMap[room.floor]) {
            floorsMap[room.floor] = [];
          }
          // Ensure status is properly set - check both lowercase and uppercase
          const roomStatus = (room.status?.toLowerCase() === 'occupied') ? 'Occupied' : 'Available';
          
          floorsMap[room.floor].push({
            ...room,  // Spread first
            number: room.room_number,
            type: room.room_type,
            status: roomStatus  // Then override with capitalized status
          });
        });
        
        // Convert to array format
        const floorsArray = Object.keys(floorsMap).map(floor => ({
          floor: parseInt(floor),
          rooms: floorsMap[floor]
        })).sort((a, b) => a.floor - b.floor);
        
        setFloors(floorsArray);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setLoading(false);
    }
  };

  const fetchRoomDetails = async (roomNumber) => {
    try {
      const response = await fetch(`https://hs.learninghub.ind.in/api/admin/room/${roomNumber}`);
      const data = await response.json();
      
      if (data.success) {
        const room = data.room;
        
        if (room.status === 'occupied' && room.guests) {
          // Format occupied room data
          setSelectedRoom({
            number: room.room_number,
            type: room.room_type,
            status: 'Occupied',
            guests: room.guests,
            checkIn: room.check_in_date,
            checkOut: room.check_out_date,
            nights: room.number_of_nights,
            totalAmount: `₹${parseFloat(room.total_amount).toLocaleString('en-IN')}`,
            advancePaid: `₹${parseFloat(room.advance_paid).toLocaleString('en-IN')}`,
            balanceDue: `₹${parseFloat(room.balance_due).toLocaleString('en-IN')}`,
            bookingId: room.booking_code,
            bookingType: room.booking_type,
            isOccupied: true
          });
        } else {
          // Format available room data
          setSelectedRoom({
            number: room.room_number,
            type: room.room_type,
            status: 'Available',
            amenities: roomAmenities[room.room_type],
            price_per_night: room.price_per_night,
            isOccupied: false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching room details:', error);
    }
  };

  // Calculate room status counts
  const roomStatus = [
    { 
      type: 'Available', 
      count: floors.reduce((sum, floor) => sum + floor.rooms.filter(r => r.status === 'Available').length, 0), 
      color: '#4CAF50' 
    },
    { 
      type: 'Occupied', 
      count: floors.reduce((sum, floor) => sum + floor.rooms.filter(r => r.status === 'Occupied').length, 0), 
      color: '#f44336' 
    },
  ];

  const getStatusColor = (status) => {
    // Normalize and capitalize status
    const normalizedStatus = status?.trim();
    const capitalizedStatus = normalizedStatus?.charAt(0).toUpperCase() + normalizedStatus?.slice(1).toLowerCase();
    const colors = {
      Available: { bg: '#4CAF50', border: '#4CAF50', text: '#fff' },
      Occupied: { bg: '#f44336', border: '#f44336', text: '#fff' },
    };
    return colors[capitalizedStatus] || colors.Available;
  };

  const handleRoomClick = (room) => {
    fetchRoomDetails(room.number);
  };

  const closeModal = () => {
    setCurrentGuestIndex(0);
    setSelectedRoom(null);
    setShowAssignGuestForm(false);
  };

  const handleAssignGuest = () => {
    // Set check-in date to today when opening the form
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayDate = `${year}-${month}-${day}`;
    
    setAssignGuestData(prev => ({
      ...prev,
      checkIn: todayDate
    }));
    setShowAssignGuestForm(true);
  };

  const handleBookingTypeChange = (type) => {
    const config = bookingTypeConfig[type];
    const newGuests = Array(config.minGuests).fill(null).map(() => ({
      guestName: '',
      age: '',
      gender: 'Male',
      phone: '',
      email: '',
      address: '',
      idType: 'Aadhaar Card',
      idNumber: '',

    }));

    setAssignGuestData(prev => ({
      ...prev,
      bookingType: type,
      numberOfGuests: config.minGuests,
      guests: newGuests
    }));
    setCurrentGuestIndex(0);
  };

  const handleNumberOfGuestsChange = (count) => {
    const config = bookingTypeConfig[assignGuestData.bookingType];
    const validCount = Math.max(config.minGuests, Math.min(config.maxGuests, parseInt(count) || config.minGuests));
    
    const currentGuests = assignGuestData.guests;
    const newGuests = Array(validCount).fill(null).map((_, index) => 
      currentGuests[index] || {
        guestName: '',
        age: '',
        gender: 'Male',
        phone: '',
        email: '',
        address: '',
        idType: 'Aadhaar Card',
        idNumber: '',
        guestPhoto: null,
        idCardPhoto: null,
      }
    );

    setAssignGuestData(prev => ({
      ...prev,
      numberOfGuests: validCount,
      guests: newGuests
    }));
  };

  const handleGuestFieldChange = (index, field, value) => {
    setAssignGuestData(prev => ({
      ...prev,
      guests: prev.guests.map((guest, i) => 
        i === index ? { ...guest, [field]: value } : guest
      )
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setAssignGuestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };



  const nextGuest = () => {
    if (currentGuestIndex < assignGuestData.guests.length - 1) {
      // Validate current guest
      const currentGuest = assignGuestData.guests[currentGuestIndex];
      if (!currentGuest.guestName || !currentGuest.age || !currentGuest.phone || !currentGuest.idNumber) {
        alert('Please fill all required fields for the current guest!');
        return;
      }
      
      setCurrentGuestIndex(currentGuestIndex + 1);
    }
  };

  const previousGuest = () => {
    if (currentGuestIndex > 0) {
      setCurrentGuestIndex(currentGuestIndex - 1);
    }
  };

  const handleAssignGuestSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all guests
    for (let i = 0; i < assignGuestData.guests.length; i++) {
      const guest = assignGuestData.guests[i];
      if (!guest.guestName || !guest.age || !guest.phone || !guest.idNumber) {
        alert(`Please fill all required fields for Guest ${i + 1}!`);
        return;
      }
    }
    
    // Validate booking amount
    if (!assignGuestData.bookingAmount || parseFloat(assignGuestData.bookingAmount) <= 0) {
      alert('Please enter a valid booking amount!');
      return;
    }
    
    // Calculate number of nights
    const checkIn = new Date(assignGuestData.checkIn);
    const checkOut = new Date(assignGuestData.checkOut);
    const numberOfNights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    
    // Get room minimum price for validation
    const roomData = roomsData.find(r => r.room_number === selectedRoom.number);
    const minPrice = roomData ? roomData.price_per_night : 0;
    
    // Validate that booking amount is not less than minimum price
    const bookingAmount = parseFloat(assignGuestData.bookingAmount);
    if (bookingAmount < minPrice) {
      alert(`Booking amount cannot be less than minimum room price of ₹${minPrice.toLocaleString('en-IN')}!`);
      return;
    }
    
    // Use the entered booking amount
    const totalAmount = bookingAmount;
    
    // Prepare booking data
    const primaryGuest = assignGuestData.guests[0];
    const additionalGuests = assignGuestData.guests.slice(1);
    
    const bookingData = {
      roomNumber: selectedRoom.number,
      bookingType: assignGuestData.bookingType,
      primaryGuest: {
        name: primaryGuest.guestName,
        age: parseInt(primaryGuest.age),
        gender: primaryGuest.gender,
        phone: primaryGuest.phone,
        email: primaryGuest.email,
        address: primaryGuest.address,
        idType: primaryGuest.idType,
        idNumber: primaryGuest.idNumber
      },
      additionalGuests: additionalGuests.map(guest => ({
        name: guest.guestName,
        age: parseInt(guest.age),
        gender: guest.gender,
        phone: guest.phone,
        email: guest.email,
        address: guest.address,
        idType: guest.idType,
        idNumber: guest.idNumber
      })),
      checkIn: assignGuestData.checkIn,
      checkOut: assignGuestData.checkOut,
      numberOfNights: numberOfNights,
      numberOfGuests: assignGuestData.numberOfGuests,
      totalAmount: totalAmount,
      advancePaid: 0
    };
    
    try {
      const response = await fetch('https://hs.learninghub.ind.in/api/admin/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Booking successful!\nBooking ID: ${data.bookingId}\nBooking Type: ${assignGuestData.bookingType}\nTotal Guests: ${assignGuestData.numberOfGuests}`);
        
        // Refresh data
        fetchDashboardStats();
        fetchAllRooms();
        
        // Cleanup
        setCurrentGuestIndex(0);
        
        // Reset form
        setAssignGuestData({
          bookingType: 'Single',
          numberOfGuests: 1,
          guests: [{
            guestName: '',
            age: '',
            gender: 'Male',
            phone: '',
            email: '',
            address: '',
            idType: 'Aadhaar Card',
            idNumber: '',
            guestPhoto: null,
            idCardPhoto: null,
          }],
          checkIn: '',
          checkOut: '',
        });
        setShowAssignGuestForm(false);
        setSelectedRoom(null);
      } else {
        alert(`Booking failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Error creating booking. Please try again.');
    }
  };

  const handleCheckout = async (roomNumber) => {
    // Check if there's pending payment
    if (selectedRoom && selectedRoom.balanceDue) {
      // Extract numeric value from balance due string (e.g., "₹5,000" -> 5000)
      const balanceAmount = parseFloat(selectedRoom.balanceDue.replace(/[₹,]/g, ''));
      
      if (balanceAmount > 0) {
        alert(`Cannot checkout! Pending payment of ${selectedRoom.balanceDue} must be cleared first.\n\nPlease collect the balance payment before checkout.`);
        return;
      }
    }
    
    if (!window.confirm('Are you sure you want to checkout this guest?')) {
      return;
    }
    
    try {
      const response = await fetch(`https://hs.learninghub.ind.in/api/admin/checkout/${roomNumber}`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Checkout successful!');
        
        // Refresh data
        fetchDashboardStats();
        fetchAllRooms();
        closeModal();
      } else {
        alert(`Checkout failed: ${data.message}`);
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      alert('Error during checkout. Please try again.');
    }
  };

  const styles = {
    dashboard: {
      width: '100%',
    },
    header: {
      marginBottom: '2rem',
    },
    headerTitle: {
      fontSize: '2rem',
      color: '#333',
      marginBottom: '0.5rem',
      fontWeight: 600,
    },
    headerSubtitle: {
      fontSize: '1rem',
      color: '#666',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    statCard: {
      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
      padding: '1.5rem',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      transition: 'all 0.3s ease',
      border: '1px solid #e0e0e0',
    },
    statIcon: {
      fontSize: '3rem',
      width: '70px',
      height: '70px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '14px',
      background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    },
    statInfo: {
      flex: 1,
    },
    statTitle: {
      fontSize: '0.875rem',
      color: '#666',
      marginBottom: '0.25rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    statValue: {
      fontSize: '1.8rem',
      fontWeight: 700,
      marginBottom: '0.25rem',
    },
    statChange: {
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      fontWeight: 600,
    },
    roomStatusSection: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 600,
      marginBottom: '1.5rem',
      color: '#333',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    statusGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
    },
    statusCard: {
      padding: '1.5rem',
      borderRadius: '12px',
      border: '2px solid',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
    },
    statusType: {
      fontSize: '1rem',
      fontWeight: 600,
      color: 'white',
    },
    statusCount: {
      fontSize: '2.5rem',
      fontWeight: 700,
      color: 'white',
    },
    floorsSection: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    },
    floorCard: {
      marginBottom: '2rem',
    },
    floorHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1rem',
      padding: '1rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '12px',
      color: 'white',
    },
    floorNumber: {
      fontSize: '2rem',
      fontWeight: 700,
      width: '60px',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '12px',
      backdropFilter: 'blur(10px)',
    },
    floorInfo: {
      flex: 1,
    },
    floorTitle: {
      fontSize: '1.3rem',
      fontWeight: 600,
      marginBottom: '0.25rem',
    },
    floorStats: {
      fontSize: '0.9rem',
      opacity: 0.9,
    },
    roomsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '1rem',
    },
    roomCard: {
      padding: '1.5rem',
      borderRadius: '12px',
      border: '2px solid',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.75rem',
      position: 'relative',
      overflow: 'hidden',
    },
    roomNumber: {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
    roomType: {
      fontSize: '0.875rem',
      fontWeight: 500,
      opacity: 0.9,
    },
    roomStatus: {
      fontSize: '0.75rem',
      padding: '0.25rem 0.75rem',
      borderRadius: '20px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem',
      backdropFilter: 'blur(5px)',
    },
    modalContent: {
      background: 'white',
      borderRadius: '20px',
      maxWidth: '1000px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      position: 'relative',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    modalHeader: {
      padding: '2rem',
      borderBottom: '2px solid #f0f0f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderRadius: '20px 20px 0 0',
    },
    modalTitle: {
      fontSize: '1.8rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    modalClose: {
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '1.5rem',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
    },
    modalBody: {
      padding: '2rem',
    },
    modalSection: {
      marginBottom: '2rem',
    },
    divider: {
      height: '2px',
      background: 'linear-gradient(90deg, transparent, #e0e0e0, transparent)',
      margin: '2rem 0',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    infoLabel: {
      fontSize: '0.875rem',
      color: '#666',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    infoValue: {
      fontSize: '1.1rem',
      color: '#333',
      fontWeight: 500,
    },
    amenitiesList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '0.75rem',
      marginTop: '1rem',
    },
    amenityItem: {
      padding: '0.75rem',
      background: '#f8f9fa',
      borderRadius: '8px',
      fontSize: '0.95rem',
      color: '#333',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      border: '1px solid #e0e0e0',
    },
    guestCard: {
      background: '#f8f9fa',
      padding: '2rem',
      borderRadius: '16px',
      marginBottom: '1.5rem',
      border: '2px solid #e0e0e0',
    },
    guestHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    guestAvatar: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.8rem',
      fontWeight: 700,
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    },
    guestName: {
      fontSize: '1.4rem',
      fontWeight: 700,
      color: '#333',
      marginBottom: '0.25rem',
    },
    guestBadge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    paymentSection: {
      background: '#f8f9fa',
      padding: '2rem',
      borderRadius: '16px',
      border: '2px solid #e0e0e0',
    },
    paymentRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    paymentLabel: {
      fontSize: '1.1rem',
      color: '#666',
      fontWeight: 600,
    },
    paymentValue: {
      fontSize: '1.3rem',
      fontWeight: 700,
      color: '#333',
    },
    balanceDue: {
      color: '#f44336',
      fontSize: '1.8rem',
    },
    actionButtons: {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem',
    },
    actionButton: {
      flex: 1,
      padding: '1rem 2rem',
      borderRadius: '12px',
      border: 'none',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    formSection: {
      marginTop: '2rem',
      padding: '2rem',
      background: '#f8f9fa',
      borderRadius: '16px',
      border: '2px solid #e0e0e0',
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    formLabel: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#333',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    formInput: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '2px solid #e0e0e0',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
    },
    formSelect: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '2px solid #e0e0e0',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      cursor: 'pointer',
    },
    bookingTypeButtons: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    bookingTypeButton: {
      padding: '1.2rem',
      borderRadius: '12px',
      border: '2px solid #e0e0e0',
      background: 'white',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.5rem',
      textAlign: 'center',
    },
    bookingTypeIcon: {
      fontSize: '2rem',
    },
    guestNavigation: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '12px',
      color: 'white',
      marginBottom: '1.5rem',
    },
    guestIndicator: {
      fontSize: '1.2rem',
      fontWeight: 600,
    },
    navButton: {
      padding: '0.5rem 1rem',
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      borderRadius: '8px',
      color: 'white',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 600,
      transition: 'all 0.3s ease',
    },
    cameraSection: {
      marginTop: '2rem',
      padding: '2rem',
      background: '#f8f9fa',
      borderRadius: '16px',
      border: '2px solid #e0e0e0',
    },
    cameraContainer: {
      position: 'relative',
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      borderRadius: '16px',
      overflow: 'hidden',
      background: '#000',
    },
    video: {
      width: '100%',
      display: 'block',
      borderRadius: '16px',
    },
    canvas: {
      display: 'none',
    },
    capturedImage: {
      width: '100%',
      borderRadius: '16px',
      border: '2px solid #4CAF50',
    },
    cameraButtons: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    cameraButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '12px',
      border: 'none',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    submitButton: {
      flex: 1,
      padding: '1rem 2rem',
      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1.1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    cancelButton: {
      flex: 1,
      padding: '1rem 2rem',
      background: 'linear-gradient(135deg, #f44336 0%, #da190b 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1.1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  const currentGuest = assignGuestData.guests[currentGuestIndex];

  return (
    <div style={styles.dashboard}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>🏨 Hotel Management Dashboard</h1>
        <p style={styles.headerSubtitle}>Manage rooms, guests, and bookings efficiently</p>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        {stats.map((stat, index) => (
          <div
            key={index}
            style={styles.statCard}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
            }}
          >
            <div style={styles.statIcon}>{stat.icon}</div>
            <div style={styles.statInfo}>
              <div style={styles.statTitle}>{stat.title}</div>
              <div style={{ ...styles.statValue, color: stat.color }}>{stat.value}</div>
              <div style={{ ...styles.statChange, color: stat.changeType === 'increase' ? '#4CAF50' : '#f44336' }}>
                <span>{stat.changeType === 'increase' ? '▲' : '▼'}</span>
                <span>{stat.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Room Status Overview */}
      <div style={styles.roomStatusSection}>
        <h2 style={styles.sectionTitle}>
          <span>📊</span>
          Room Status Overview
        </h2>
        <div style={styles.statusGrid}>
          {roomStatus.map((status, index) => (
            <div
              key={index}
              style={{
                ...styles.statusCard,
                borderColor: status.color,
                background: status.color,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${status.color}40`;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={styles.statusType}>{status.type}</div>
              <div style={styles.statusCount}>{status.count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Floors and Rooms */}
      <div style={styles.floorsSection}>
        <h2 style={styles.sectionTitle}>
          <span>🏢</span>
          Rooms by Floor
        </h2>

        {floors.map((floor) => (
          <div key={floor.floor} style={styles.floorCard}>
            <div style={styles.floorHeader}>
              <div style={styles.floorNumber}>{floor.floor}</div>
              <div style={styles.floorInfo}>
                <div style={styles.floorTitle}>Floor {floor.floor}</div>
                <div style={styles.floorStats}>
                  {floor.rooms.filter((r) => r.status === 'Available').length} Available • 
                  {floor.rooms.filter((r) => r.status === 'Occupied').length} Occupied
                </div>
              </div>
            </div>

            <div style={styles.roomsGrid}>
              {floor.rooms.map((room) => {
                const colors = getStatusColor(room.status);
                return (
                  <div
                    key={room.number}
                    style={{
                      ...styles.roomCard,
                      borderColor: colors.border,
                      background: colors.bg,
                      color: colors.text,
                    }}
                    onClick={() => handleRoomClick(room)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px) scale(1.05)';
                      e.currentTarget.style.boxShadow = `0 10px 30px ${colors.border}40`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={styles.roomNumber}>{room.number}</div>
                    <div style={styles.roomType}>{room.type}</div>
                    <div
                      style={{
                        ...styles.roomStatus,
                        background: 'rgba(255,255,255,0.3)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {room.status}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Room Details Modal */}
      {selectedRoom && (
        <div style={styles.modal} onClick={closeModal}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                <span>🚪</span>
                Room {selectedRoom.number} - {selectedRoom.type}
              </h2>
              <button style={styles.modalClose} onClick={closeModal}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* AVAILABLE ROOM - Show Amenities and Assign Option */}
              {!selectedRoom.isOccupied && (
                <>
                  <div style={styles.modalSection}>
                    <h3 style={styles.sectionTitle}>✨ Room Amenities</h3>
                    
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Room Size</span>
                        <span style={styles.infoValue}>{selectedRoom.amenities.size}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Bed Type</span>
                        <span style={styles.infoValue}>{selectedRoom.amenities.bedType}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Max Occupancy</span>
                        <span style={styles.infoValue}>{selectedRoom.amenities.maxOccupancy} guests</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Price Range</span>
                        <span style={styles.infoValue}>{selectedRoom.amenities.price}</span>
                      </div>
                    </div>

                    <div style={styles.amenitiesList}>
                      {selectedRoom.amenities.amenities.map((amenity, index) => (
                        <div key={index} style={styles.amenityItem}>
                          {amenity}
                        </div>
                      ))}
                    </div>
                  </div>

                  {!showAssignGuestForm && (
                    <div style={styles.actionButtons}>
                      <button
                        style={{
                          ...styles.actionButton,
                          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                          color: 'white',
                        }}
                        onClick={handleAssignGuest}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(76, 175, 80, 0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        👤 Assign Guest
                      </button>
                    </div>
                  )}

                  {/* Guest Assignment Form */}
                  {showAssignGuestForm && (
                    <div style={styles.formSection}>
                      <h3 style={styles.sectionTitle}>📝 Guest Information</h3>
                      
                      <form onSubmit={handleAssignGuestSubmit}>
                        {/* Booking Type Selection */}
                        <div style={{ marginBottom: '2rem' }}>
                          <div style={styles.formLabel}>Booking Type</div>
                          <div style={styles.bookingTypeButtons}>
                            {Object.keys(bookingTypeConfig).map((type) => {
                              const config = bookingTypeConfig[type];
                              return (
                                <button
                                  key={type}
                                  type="button"
                                  style={{
                                    ...styles.bookingTypeButton,
                                    background: assignGuestData.bookingType === type ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                    color: assignGuestData.bookingType === type ? 'white' : '#333',
                                    borderColor: assignGuestData.bookingType === type ? '#667eea' : '#e0e0e0',
                                  }}
                                  onClick={() => handleBookingTypeChange(type)}
                                  onMouseOver={(e) => {
                                    if (assignGuestData.bookingType !== type) {
                                      e.currentTarget.style.borderColor = '#667eea';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                    }
                                  }}
                                  onMouseOut={(e) => {
                                    if (assignGuestData.bookingType !== type) {
                                      e.currentTarget.style.borderColor = '#e0e0e0';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }
                                  }}
                                >
                                  <span style={styles.bookingTypeIcon}>{config.icon}</span>
                                  <span>{config.label}</span>
                                  <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{config.description}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Number of Guests (for Family and Group) */}
                        {(assignGuestData.bookingType === 'Family' || assignGuestData.bookingType === 'Group of Friends') && (
                          <div style={{ marginBottom: '2rem' }}>
                            <div style={styles.formGroup}>
                              <label style={styles.formLabel}>Number of Guests</label>
                              <input
                                type="number"
                                value={assignGuestData.numberOfGuests}
                                onChange={(e) => handleNumberOfGuestsChange(e.target.value)}
                                style={styles.formInput}
                                min={bookingTypeConfig[assignGuestData.bookingType].minGuests}
                                max={bookingTypeConfig[assignGuestData.bookingType].maxGuests}
                                required
                              />
                              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                Min: {bookingTypeConfig[assignGuestData.bookingType].minGuests}, 
                                Max: {bookingTypeConfig[assignGuestData.bookingType].maxGuests}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Guest Navigation */}
                        {assignGuestData.numberOfGuests > 1 && (
                          <div style={styles.guestNavigation}>
                            <button
                              type="button"
                              style={{
                                ...styles.navButton,
                                opacity: currentGuestIndex === 0 ? 0.5 : 1,
                                cursor: currentGuestIndex === 0 ? 'not-allowed' : 'pointer',
                              }}
                              onClick={previousGuest}
                              disabled={currentGuestIndex === 0}
                            >
                              ← Previous
                            </button>
                            <div style={styles.guestIndicator}>
                              Guest {currentGuestIndex + 1} of {assignGuestData.numberOfGuests}
                            </div>
                            <button
                              type="button"
                              style={{
                                ...styles.navButton,
                                opacity: currentGuestIndex === assignGuestData.numberOfGuests - 1 ? 0.5 : 1,
                                cursor: currentGuestIndex === assignGuestData.numberOfGuests - 1 ? 'not-allowed' : 'pointer',
                              }}
                              onClick={nextGuest}
                              disabled={currentGuestIndex === assignGuestData.numberOfGuests - 1}
                            >
                              Next →
                            </button>
                          </div>
                        )}

                        {/* Current Guest Details */}
                        <div style={styles.formGrid}>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Full Name *</label>
                            <input
                              type="text"
                              value={currentGuest.guestName}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'guestName', e.target.value)}
                              style={styles.formInput}
                              required
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Age *</label>
                            <input
                              type="number"
                              value={currentGuest.age}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'age', e.target.value)}
                              style={styles.formInput}
                              required
                              min="1"
                              max="120"
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Gender *</label>
                            <select
                              value={currentGuest.gender}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'gender', e.target.value)}
                              style={styles.formSelect}
                              required
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            >
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Phone Number *</label>
                            <input
                              type="tel"
                              value={currentGuest.phone}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'phone', e.target.value)}
                              style={styles.formInput}
                              required
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Email</label>
                            <input
                              type="email"
                              value={currentGuest.email}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'email', e.target.value)}
                              style={styles.formInput}
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Address *</label>
                            <input
                              type="text"
                              value={currentGuest.address}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'address', e.target.value)}
                              style={styles.formInput}
                              required
                              placeholder="Enter address manually"
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>ID Type *</label>
                            <select
                              value={currentGuest.idType}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'idType', e.target.value)}
                              style={styles.formSelect}
                              required
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            >
                              <option value="Aadhaar Card">Aadhaar Card</option>
                              <option value="PAN Card">PAN Card</option>
                              <option value="Driving License">Driving License</option>
                              <option value="Passport">Passport</option>
                              <option value="Voter ID">Voter ID</option>
                            </select>
                          </div>

                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>ID Number *</label>
                            <input
                              type="text"
                              value={currentGuest.idNumber}
                              onChange={(e) => handleGuestFieldChange(currentGuestIndex, 'idNumber', e.target.value)}
                              style={styles.formInput}
                              required
                              onFocus={(e) => e.target.style.borderColor = '#667eea'}
                              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                            />
                          </div>
                        </div>

                        {/* Check-in/out Dates (only for first guest) */}
                        {currentGuestIndex === 0 && (
                          <div style={{ ...styles.formGrid, marginTop: '1.5rem' }}>
                            <div style={styles.formGroup}>
                              <label style={styles.formLabel}>Check-in Date * (Today Only)</label>
                              <input
                                type="date"
                                name="checkIn"
                                value={assignGuestData.checkIn || getTodayDate()}
                                onChange={handleFormChange}
                                style={styles.formInput}
                                required
                                min={getTodayDate()}
                                max={getTodayDate()}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                              />
                            </div>

                            <div style={styles.formGroup}>
                              <label style={styles.formLabel}>Check-out Date *</label>
                              <input
                                type="date"
                                name="checkOut"
                                value={assignGuestData.checkOut}
                                onChange={handleFormChange}
                                style={styles.formInput}
                                required
                                min={getTodayDate()}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                              />
                            </div>

                            <div style={styles.formGroup}>
                              <label style={styles.formLabel}>
                                Booking Amount * (Min: ₹{selectedRoom.price_per_night ? selectedRoom.price_per_night.toLocaleString('en-IN') : '0'})
                              </label>
                              <input
                                type="number"
                                name="bookingAmount"
                                value={assignGuestData.bookingAmount}
                                onChange={handleFormChange}
                                style={styles.formInput}
                                required
                                min={selectedRoom.price_per_night || 0}
                                placeholder={`Enter amount (Minimum ₹${selectedRoom.price_per_night || 0})`}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                              />
                            </div>
                          </div>
                        )}


                        {/* Form Buttons */}
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                          {currentGuestIndex === assignGuestData.numberOfGuests - 1 ? (
                            <>
                              <button
                                type="submit"
                                style={styles.submitButton}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                ✓ Confirm Booking ({assignGuestData.numberOfGuests} Guest{assignGuestData.numberOfGuests > 1 ? 's' : ''})
                              </button>
                              <button
                                type="button"
                                style={styles.cancelButton}
                                onClick={() => setShowAssignGuestForm(false)}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(244, 67, 54, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                ✕ Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                style={{...styles.submitButton, background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'}}
                                onClick={nextGuest}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(33, 150, 243, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                Next Guest ({currentGuestIndex + 2}/{assignGuestData.numberOfGuests}) →
                              </button>
                              <button
                                type="button"
                                style={styles.cancelButton}
                                onClick={() => setShowAssignGuestForm(false)}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(244, 67, 54, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                              >
                                ✕ Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </form>
                    </div>
                  )}
                </>
              )}

              {/* OCCUPIED ROOM - Show Guest Details */}
              {selectedRoom.isOccupied && (
                <>
                  {/* Booking Information */}
                  <div style={styles.modalSection}>
                    <h3 style={styles.sectionTitle}>📅 Booking Information</h3>
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Booking ID</span>
                        <span style={styles.infoValue}>{selectedRoom.bookingId}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Booking Type</span>
                        <span style={styles.infoValue}>
                          {bookingTypeConfig[selectedRoom.bookingType]?.icon || '👤'} {selectedRoom.bookingType}
                        </span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Check-in Date</span>
                        <span style={styles.infoValue}>{selectedRoom.checkIn}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Check-out Date</span>
                        <span style={styles.infoValue}>{selectedRoom.checkOut}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Number of Nights</span>
                        <span style={styles.infoValue}>{selectedRoom.nights} nights</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Number of Guests</span>
                        <span style={styles.infoValue}>{selectedRoom.guests.length} guest(s)</span>
                      </div>
                    </div>
                  </div>

                  <div style={styles.divider}></div>

                  {/* Guest Details */}
                  <div style={styles.modalSection}>
                    <h3 style={styles.sectionTitle}>👥 Guest Details</h3>
                    {selectedRoom.guests.map((guest, index) => (
                      <div key={index} style={styles.guestCard}>
                        <div style={styles.guestHeader}>
                          {guest.photo_url ? (
                            <img 
                              src={guest.photo_url} 
                              alt={guest.name}
                              style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                              }}
                            />
                          ) : (
                            <div style={styles.guestAvatar}>{guest.name.charAt(0)}</div>
                          )}
                          <div style={{ flex: 1 }}>
                            <h4 style={styles.guestName}>{guest.name}</h4>
                            <span style={styles.guestBadge}>
                              {index === 0 ? 'Primary Guest' : `Guest ${index + 1}`}
                            </span>
                          </div>
                        </div>
                        
                        <div style={styles.infoGrid}>
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Age</span>
                            <span style={styles.infoValue}>{guest.age} years</span>
                          </div>
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Gender</span>
                            <span style={styles.infoValue}>{guest.gender}</span>
                          </div>
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Phone Number</span>
                            <span style={styles.infoValue}>{guest.phone}</span>
                          </div>
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Email Address</span>
                            <span style={styles.infoValue}>{guest.email || '-'}</span>
                          </div>
                          <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Address</span>
                            <span style={styles.infoValue}>{guest.address || '-'}</span>
                          </div>
                        </div>

                        <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: '#e3f2fd', borderRadius: '12px', border: '2px solid #2196F3' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div style={styles.infoItem}>
                              <span style={styles.infoLabel}>🆔 ID Type</span>
                              <span style={{ ...styles.infoValue, fontWeight: 700, color: '#2c5aa0' }}>{guest.id_type}</span>
                            </div>
                            <div style={styles.infoItem}>
                              <span style={styles.infoLabel}>📋 ID Number</span>
                              <span style={{ ...styles.infoValue, fontWeight: 700, color: '#2c5aa0', fontSize: '1.3rem' }}>
                                {guest.id_number}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={styles.divider}></div>

                  {/* Payment Details */}
                  <div style={styles.modalSection}>
                    <h3 style={styles.sectionTitle}>💰 Payment Details</h3>
                    <div style={styles.paymentSection}>
                      <div style={styles.paymentRow}>
                        <span style={styles.paymentLabel}>Total Amount</span>
                        <span style={styles.paymentValue}>{selectedRoom.totalAmount}</span>
                      </div>
                      <div style={styles.paymentRow}>
                        <span style={styles.paymentLabel}>Advance Paid</span>
                        <span style={{ ...styles.paymentValue, color: '#4CAF50' }}>
                          {selectedRoom.advancePaid}
                        </span>
                      </div>
                      <div style={{ ...styles.paymentRow, paddingTop: '1.5rem', borderTop: '3px dashed #FF9800', marginTop: '1rem' }}>
                        <span style={{ ...styles.paymentLabel, fontSize: '1.3rem', fontWeight: 700 }}>
                          Balance Due
                        </span>
                        <span style={{ ...styles.paymentValue, ...styles.balanceDue }}>
                          {selectedRoom.balanceDue}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <div style={styles.actionButtons}>
                    <button
                      style={{
                        ...styles.actionButton,
                        background: 'linear-gradient(135deg, #f44336 0%, #da190b 100%)',
                        color: 'white',
                      }}
                      onClick={() => handleCheckout(selectedRoom.number)}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(244, 67, 54, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      🚪 Checkout Guest
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;